import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaExclamationTriangle } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "warning") return "#f59e0b";
    if ($type === "tip") return "#10b981";
    if ($type === "critical") return "#ef4444";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "tip") return "rgba(16, 185, 129, 0.08)";
    if ($type === "critical") return "rgba(239, 68, 68, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "warning") return "#f59e0b";
      if ($type === "tip") return "#10b981";
      if ($type === "critical") return "#ef4444";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ScenarioBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px dashed ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    color: ${({ theme }) => theme.iconColor};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .scenario-text {
    font-style: italic;
    margin: 0.75rem 0;
    padding: 0.75rem;
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.05)"};
    border-radius: 4px;
  }

  .red-flags {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid ${({ theme }) => theme.iconColor};

    strong {
      color: #ef4444;
      display: block;
      margin-bottom: 0.5rem;
    }

    ul {
      margin: 0;
      padding-left: 1.5rem;

      li {
        margin-bottom: 0.5rem;
      }
    }
  }
`;

const VerificationStep = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  background-color: ${({ theme }) =>
    theme.isdarkmode ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.08)"};
  border-left: 4px solid #10b981;
  border-radius: 4px;
  color: ${({ theme }) => theme.text};

  strong {
    color: #10b981;
    display: block;
    margin-bottom: 0.5rem;
  }

  ul {
    margin: 0.5rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const SocialEngineeringGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.SOCIAL_ENGINEERING);

  // const heroImageSrc = "/images/guides/social-engineering.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Social Engineering Red Flags"
        seoTitle="Social Engineering Red Flags: Common Tactics & How to Respond | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Learn common social engineering tactics used against small businesses, how to recognize them, verify requests, and report threats."
      ogTitle="Social Engineering Red Flags: Common Tactics & How to Respond | CSFSafe"
      ogDescription="Recognize social engineering attacks targeting your team. Verification steps and reporting procedures for SMBs."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Social engineering attacks and human manipulation"
      publishedLabel="Quick Reference Guide — July 21, 2026"
      showCTA={true}
    >
      <Paragraph>
        Social engineering is manipulation—attackers use psychology, urgency,
        and authority to trick people into revealing information, transferring
        money, or granting access. This guide helps you recognize common tactics
        and respond appropriately.
      </Paragraph>

      <CalloutBox $type="critical">
        <strong>Core Rule: When in Doubt, Verify Independently</strong>
        <ul>
          <li>
            If someone requests urgent action, sensitive information, or access,
            STOP and verify they are who they claim to be
          </li>
          <li>
            Use a known phone number or contact method from your organization,
            not information provided by the caller
          </li>
          <li>
            Never provide passwords, credit card numbers, or access codes via
            email, phone, or messaging apps
          </li>
        </ul>
      </CalloutBox>

      <H2>Common Social Engineering Tactics</H2>

      <H3>1. Pretexting (Creating a False Scenario)</H3>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> Common Pretext Scenarios
        </h4>
        <div className="scenario-text">
          "Hi, I'm from IT support. We detected a security issue on your
          computer. I need you to click this link and enter your password so I
          can fix it."
        </div>
        <div className="red-flags">
          <strong>Red Flags:</strong>
          <ul>
            <li>Caller claims to be from IT but you don't recognize them</li>
            <li>They create urgency ("act now" / "your account will be locked")</li>
            <li>They ask for passwords or credentials</li>
            <li>
              They send unsolicited messages or links (legitimate IT wouldn't)
            </li>
            <li>
              The sender's email or phone number doesn't match your IT department
            </li>
          </ul>
        </div>
      </ScenarioBox>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> Authority Impersonation
        </h4>
        <div className="scenario-text">
          "This is the CEO. We need you to wire $50,000 to this vendor
          immediately. Keep this confidential and don't ask questions."
        </div>
        <div className="red-flags">
          <strong>Red Flags:</strong>
          <ul>
            <li>
              Attacker impersonates a senior executive or authority figure (CEO,
              FBI, IRS, bank)
            </li>
            <li>
              Demands secrecy ("don't tell anyone," "keep this quiet")
            </li>
            <li>
              Creates false urgency or consequences ("or you're fired," "account
              closure")
            </li>
            <li>
              The tone or writing style doesn't match what you'd expect from that
              person
            </li>
            <li>Requests unusual payment method or unusual recipient</li>
          </ul>
        </div>
      </ScenarioBox>

      <H3>2. Phishing (Fake Communications)</H3>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> Phishing Email Tricks
        </h4>
        <div className="scenario-text">
          Email appears to be from your bank: "Your account has unusual activity.
          Verify your account here: bankofamerica-login.phish.com"
        </div>
        <div className="red-flags">
          <strong>Red Flags:</strong>
          <ul>
            <li>
              Email appears to be from a trusted company but sender email is
              slightly off
            </li>
            <li>Generic greeting ("Dear Customer" instead of your name)</li>
            <li>Requests to click a link to "verify" or "confirm" information</li>
            <li>
              Linked URL doesn't match the company domain (hover to check)
            </li>
            <li>Attachment with unexpected file type (.exe, .zip, etc.)</li>
          </ul>
        </div>
      </ScenarioBox>

      <H3>3. Pretexting for Access (Vishing - Voice Phishing)</H3>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> Phone Call Attack
        </h4>
        <div className="scenario-text">
          "Hi, this is John from Tech Support. Your account has been flagged for
          suspicious activity. Can you verify your employee ID and password so I
          can unlock it?"
        </div>
        <div className="red-flags">
          <strong>Red Flags:</strong>
          <ul>
            <li>Unsolicited call asking for sensitive information</li>
            <li>
              Caller claims to be from your company's IT but you don't recognize
              them
            </li>
            <li>
              They claim there's a problem and need to "verify" your details
            </li>
            <li>
              Sound quality is poor or background noise is unusual (could be
              spoofed)
            </li>
            <li>They pressure you to act quickly</li>
          </ul>
        </div>
      </ScenarioBox>

      <H3>4. Quid Pro Quo (Offering Something in Return)</H3>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> Reward or Favor Tactics
        </h4>
        <div className="scenario-text">
          "I found a USB drive in your parking lot with 'Payroll' written on it.
          I'll return it if you confirm your employee number so I can deliver it
          safely."
        </div>
        <div className="red-flags">
          <strong>Red Flags:</strong>
          <ul>
            <li>Someone offers help or a reward in exchange for information</li>
            <li>
              They ask for "just a little information" to complete a transaction
            </li>
            <li>The offer seems too convenient or good to be true</li>
            <li>
              They create obligation ("I'm helping you, so you should help me")
            </li>
          </ul>
        </div>
      </ScenarioBox>

      <H3>5. Baiting (Leaving Physical Items)</H3>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> Physical Baiting Tactics
        </h4>
        <div className="scenario-text">
          A USB drive labeled "Executive Salaries 2024" is left in the lobby or
          parking lot. An employee picks it up and plugs it into their computer.
        </div>
        <div className="red-flags">
          <strong>Red Flags:</strong>
          <ul>
            <li>Finding media (USB, CD) with a curiosity-inducing label</li>
            <li>
              Plugging unknown devices into company computers
            </li>
            <li>
              Device appears to have been left in an unusual place
            </li>
          </ul>
        </div>
      </ScenarioBox>

      <H3>6. Tailgating / Shoulder Surfing (Physical Access)</H3>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> Physical Access Tricks
        </h4>
        <div className="scenario-text">
          An attacker follows an employee through a locked door by pretending to
          be a vendor or contractor. Or they position themselves to watch
          someone enter a PIN or password.
        </div>
        <div className="red-flags">
          <strong>Red Flags:</strong>
          <ul>
            <li>
              Someone you don't recognize trying to enter through a secure door
            </li>
            <li>
              Person standing too close while you're entering a PIN or password
            </li>
            <li>Someone asking to "follow you in" after you unlock a door</li>
          </ul>
        </div>
      </ScenarioBox>

      <H2>Verification Steps: The Right Way to Respond</H2>

      <H3>Before You Share Any Information or Click Any Link</H3>

      <VerificationStep>
        <strong>Rule 1: Verify the Sender Independently</strong>
        <ul>
          <li>
            If someone calls claiming to be from your bank, hang up and call your
            bank's official number (from your statement or website, not the phone
            number they provided)
          </li>
          <li>
            If an email appears to be from your company, contact that department
            directly using a known phone number or email
          </li>
          <li>
            Never use contact information provided in the suspicious message—find
            your own
          </li>
        </ul>
      </VerificationStep>

      <VerificationStep>
        <strong>Rule 2: Ask for More Details (to Confirm Legitimacy)</strong>
        <ul>
          <li>
            "Can you provide a case/ticket number I can reference?" (Real
            companies track requests)
          </li>
          <li>
            "What is the exact issue you're calling about?" (Social engineers may
            not have details)
          </li>
          <li>
            "Let me have a colleague verify this" (Legitimate requests don't
            require immediate secrecy)
          </li>
          <li>
            "Can I call you back at the main company number?" (Real employees have
            a callback number)
          </li>
        </ul>
      </VerificationStep>

      <VerificationStep>
        <strong>Rule 3: Watch for Pressure and Secrecy</strong>
        <ul>
          <li>
            Legitimate requests don't demand urgency or secrecy
          </li>
          <li>
            Real companies don't say "don't tell anyone" or "this is confidential"
            when asking routine questions
          </li>
          <li>
            If someone pressures you, it's a red flag—take time to verify
          </li>
        </ul>
      </VerificationStep>

      <VerificationStep>
        <strong>Rule 4: Hang Up and Reinitiate Contact</strong>
        <ul>
          <li>
            If you receive a suspicious call, hang up without providing info
          </li>
          <li>
            Look up the company's official phone number and call them back
          </li>
          <li>
            Ask if anyone just called you—legitimate organizations will know
          </li>
        </ul>
      </VerificationStep>

      <H2>Reporting Social Engineering Attempts</H2>

      <H3>Who to Notify</H3>

      <CalloutBox $type="tip">
        <strong>Internal Reporting</strong>
        <ul>
          <li>
            Report to your IT security team or help desk immediately
          </li>
          <li>
            Forward suspicious emails (don't click anything first)
          </li>
          <li>
            Report phone calls to your manager or security officer
          </li>
          <li>
            Note: You won't be punished for reporting a mistake—that's the point
          </li>
        </ul>
      </CalloutBox>

      <H3>Information to Include</H3>

      <CalloutBox $type="tip">
        <strong>Details to Provide When Reporting</strong>
        <ul>
          <li>
            <strong>For email:</strong> Who it claimed to be from, the email
            address, subject line, and any URLs or attachments
          </li>
          <li>
            <strong>For calls:</strong> What they claimed, phone number (if
            displayed), time of call, and what they asked for
          </li>
          <li>
            <strong>For physical:</strong> When and where it occurred, description
            of the person, and what they accessed or attempted to access
          </li>
          <li>
            Whether anyone clicked a link, opened an attachment, or disclosed
            information
          </li>
        </ul>
      </CalloutBox>

      <H2>Real World Examples & Responses</H2>

      <Paragraph>
        <strong>Scenario 1: Fake IT Support Call</strong>
      </Paragraph>
      <CalloutBox $type="warning">
        <strong>What They Say:</strong> "Hi, this is Jake from Microsoft Support. We detected malware on your computer. I need to remote into it to fix it."
        <br />
        <strong>What You Should Do:</strong>
        <ul>
          <li>Hang up immediately</li>
          <li>Do NOT let them remote in</li>
          <li>Call your actual IT department (look up the number yourself)</li>
          <li>Report to security</li>
        </ul>
      </CalloutBox>

      <Paragraph>
        <strong>Scenario 2: Business Email Compromise (CEO Fraud)</strong>
      </Paragraph>
      <CalloutBox $type="warning">
        <strong>What They Say:</strong> Email from "CEO": "We're acquiring a company. Wire $100k to this account immediately. Don't tell anyone."
        <br />
        <strong>What You Should Do:</strong>
        <ul>
          <li>DO NOT wire money immediately</li>
          <li>Call the CEO's office directly (look up the main number)</li>
          <li>Ask about the acquisition—legitimate execs expect verification</li>
          <li>Confirm the wire request through normal channels</li>
          <li>Report the email to security</li>
        </ul>
      </CalloutBox>

      <Paragraph>
        <strong>Scenario 3: Suspicious USB Device</strong>
      </Paragraph>
      <CalloutBox $type="warning">
        <strong>What You Find:</strong> A USB drive labeled "Q4 Bonus Spreadsheet" in the parking lot
        <br />
        <strong>What You Should Do:</strong>
        <ul>
          <li>DO NOT plug it into your work computer</li>
          <li>Turn it in to IT or security</li>
          <li>Let them analyze it safely</li>
          <li>Never plug in unknown media</li>
        </ul>
      </CalloutBox>

      <H2>Organizational Best Practices</H2>

      <Paragraph>
        Social engineering isn't just an individual issue—it's an organizational
        problem. Help your workplace implement:
      </Paragraph>

      <ul>
        <li>
          <strong>Security awareness training:</strong> Regular training on
          spotting social engineering
        </li>
        <li>
          <strong>Phishing simulations:</strong> Test employees with fake phishing
          emails to raise awareness
        </li>
        <li>
          <strong>Verification procedures:</strong> Always verify sensitive
          requests (wire transfers, access changes) through multiple channels
        </li>
        <li>
          <strong>Physical security:</strong> Badge readers, visitor logs, and
          escort policies for unfamiliar people
        </li>
        <li>
          <strong>Incident response:</strong> A clear process for reporting
          suspicious activity
        </li>
        <li>
          <strong>Culture of verification:</strong> Make it normal to ask "let me
          confirm that" before acting on requests
        </li>
      </ul>

      <H2>Quick Mental Checklist</H2>

      <CalloutBox $type="tip">
        <strong>When Someone Requests Information, Action, or Access—Ask Yourself:</strong>
        <ul>
          <li>
            Am I certain this person is who they claim to be?
          </li>
          <li>
            Is this request coming through the normal channel?
          </li>
          <li>
            Do they seem to be in a hurry or creating pressure?
          </li>
          <li>
            Are they asking for something unusual (passwords, wire transfer, access)?
          </li>
          <li>
            Are they asking me to keep it secret or not tell others?
          </li>
        </ul>
        <p style={{ marginTop: "1rem", fontWeight: "600" }}>
          If any of these answers is "yes," VERIFY INDEPENDENTLY before acting.
        </p>
      </CalloutBox>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.THREAT_TYPE.SOCIAL_ENGINEERING)}>
            Social Engineering Threats: Full Article
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.BUSINESS_EMAIL_COMPROMISE)}>
            Business Email Compromise (BEC)
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.PHISHING_THREAT)}>
            Phishing Threat Types
          </a>
        </li>
        <li>
          <a href={href(LINKS.QUICK_GUIDES.PHISHING_DETECTION)}>
            Phishing Detection Quick Guide
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/social-engineering"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

SocialEngineeringGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default SocialEngineeringGuide;
